import { memo, useCallback, useRef } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { Empty, Loader } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { ContextScope } from "./agent-route"
import type { ThreadContextState } from "@/screens/app/threads/thread-context"
import type { Editor } from "@tiptap/react"
import { ChangelogProvider } from "@/screens/app/changelog/changelog-context"
import { ChangelogEntry } from "@/screens/app/changelog/changelog-entry"
import { HelpCenterArticleView } from "@/screens/app/helpCenter/help-center-article"
import { HelpCenterArticleProvider } from "@/screens/app/helpCenter/help-center-article-context"
import { HelpCenterProvider } from "@/screens/app/helpCenter/help-center-context"
import { CenterContent } from "@/screens/app/threads/components/center-content/center-content"
import { useThreadDetailCore } from "@/screens/app/threads/hooks/useInitThread"
import { ThreadContextProvider } from "@/screens/app/threads/thread-context"

const NOOP = () => undefined

const Pending = ({ settled }: { settled: boolean }) =>
	settled ? (
		<Empty className="h-full" title="This page no longer exists" text="" />
	) : (
		<div className="flex flex-1 items-center justify-center">
			<Loader size={12} />
		</div>
	)

const ThreadPane = ({ threadId }: { threadId: string }) => {
	const core = useThreadDetailCore(threadId)
	if (!core.thread) {
		return <Pending settled={core.queryResult.type === "complete"} />
	}
	const value: ThreadContextState = {
		...core,
		goNext: NOOP,
		goPrev: NOOP,
		nextThread: null,
		prevThread: null,
		doneShortcutEnabled: false,
		// Esc belongs to the chat beside it.
		leaveOnEscape: false,
	}
	return (
		<ThreadContextProvider value={value}>
			<div className="relative flex min-w-0 flex-1 overflow-hidden">
				<CenterContent embedded />
			</div>
		</ThreadContextProvider>
	)
}

const ArticlePane = ({ articleId }: { articleId: string }) => {
	const [groups, result] = useQuery(queries.helpCenterArticlesWithGroups())
	const settled = result.type === "complete"
	const article = groups
		.flatMap((g) => g.helpCenterArticles)
		.find((a) => a.id === articleId)
	const group = groups.find((g) => g.id === article?.helpCenterGroupId)
	if (!article || !group) return <Pending settled={settled} />
	return (
		<HelpCenterProvider onItemSelected={NOOP} articlesSettled={settled}>
			<HelpCenterArticleProvider>
				<HelpCenterArticleView group={group} article={article} embedded />
			</HelpCenterArticleProvider>
		</HelpCenterProvider>
	)
}

const ChangelogPane = ({
	changelogId,
	registerEditorGetter,
}: {
	changelogId: string
	registerEditorGetter?: (getter: () => Editor | null) => void
}) => {
	const [changelogs, result] = useQuery(queries.changelogs())
	const changelog = changelogs.find((c) => c.id === changelogId)
	if (!changelog) return <Pending settled={result.type === "complete"} />
	return (
		<ChangelogProvider>
			<div className="no-scrollbar flex-1 overflow-y-auto bg-white pb-40 dark:bg-gray-900">
				<div className="flex w-full justify-center p-6">
					<div className="w-full max-w-4xl">
						<ChangelogEntry
							key={changelog.id}
							changelog={changelog}
							registerEditorGetter={registerEditorGetter}
						/>
					</div>
				</div>
			</div>
		</ChangelogProvider>
	)
}

// The changelog, article, or conversation the chat was expanded from, so the
// assistant's edits show up beside it.
export const ExpandedChatContext = memo(
	({
		scope,
		registerEditorGetter,
	}: {
		scope: ContextScope
		registerEditorGetter?: (getter: () => Editor | null) => void
	}) => (
		<div
			data-testid="agent-chat-context"
			className="flex min-h-0 w-[42%] min-w-[24rem] max-w-[56rem] flex-none flex-col overflow-hidden border-l border-gray-200 bg-white max-lg:hidden dark:border-gray-700/80 dark:bg-gray-900"
		>
			{scope.kind === "thread" && <ThreadPane threadId={scope.threadId} />}
			{scope.kind === "article" && <ArticlePane articleId={scope.articleId} />}
			{scope.kind === "changelog" && (
				<ChangelogPane
					changelogId={scope.changelogId}
					registerEditorGetter={registerEditorGetter}
				/>
			)}
		</div>
	),
)

/** Lets the expanded chat read the live changelog body, as the side panel does. */
export function useChangelogPageContext(scope: ContextScope | null) {
	const getterRef = useRef<(() => Editor | null) | null>(null)
	const register = useCallback((getter: () => Editor | null) => {
		getterRef.current = getter
	}, [])
	const isChangelog = scope?.kind === "changelog"
	const getPageContext = useCallback(() => {
		if (!isChangelog) return undefined
		const html = getterRef.current?.()?.getHTML()
		return html ? { html } : undefined
	}, [isChangelog])
	return { register, getPageContext }
}
